import { useEffect, useMemo, useState } from "react";
import { getClubs, prizeGrant, type Club, ApiError } from "../lib/api";
import { useT } from "../i18n";
import type { Lang, TFn } from "../i18n";
import { errorMessage } from "../i18n/errors";

// BCP-47 locale per app language for date label formatting.
const LOCALE: Record<Lang, string> = { kk: "kk-KZ", ru: "ru-RU", en: "en-US" };

// City names come from HJ in English ("Almaty"/"Astana"). Localize the group
// headers; the festival only has gyms in these two cities.
const CITY_LABELS: Record<string, Record<Lang, string>> = {
  almaty: { kk: "Алматы", ru: "Алматы", en: "Almaty" },
  astana: { kk: "Астана", ru: "Астана", en: "Astana" },
};
// Almaty first (the festival city), then Astana, then anything else.
const CITY_ORDER = ["almaty", "astana"];
const cityRank = (city: string | null) => {
  const i = CITY_ORDER.indexOf((city ?? "").toLowerCase());
  return i === -1 ? 99 : i;
};
function cityLabel(city: string | null, lang: Lang, t: TFn): string {
  if (!city) return t("prize.cityOther");
  return CITY_LABELS[city.toLowerCase()]?.[lang] ?? city;
}
/** Group clubs by city, ordered Almaty → Astana → other, preserving club order. */
function groupClubsByCity(clubs: Club[]): { city: string | null; clubs: Club[] }[] {
  const groups: { city: string | null; clubs: Club[] }[] = [];
  const idx = new Map<string, number>();
  for (const c of clubs) {
    const key = (c.city ?? "").toLowerCase() || "__none";
    if (!idx.has(key)) {
      idx.set(key, groups.length);
      groups.push({ city: c.city ?? null, clubs: [] });
    }
    groups[idx.get(key)!].clubs.push(c);
  }
  return groups.sort((a, b) => cityRank(a.city) - cityRank(b.city));
}

/** 3 ближайшие даты (сегодня / завтра / послезавтра) в часовом поясе Asia/Almaty. */
function almatyDateOptions(t: TFn, lang: Lang): { value: string; label: string }[] {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Almaty",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const labelFmt = new Intl.DateTimeFormat(LOCALE[lang], {
    timeZone: "Asia/Almaty",
    day: "numeric",
    month: "long",
  });
  const names = [t("prize.today"), t("prize.tomorrow"), t("prize.dayAfter")];
  const dayMs = 24 * 60 * 60 * 1000;
  return names.map((name, i) => {
    const d = new Date(Date.now() + i * dayMs);
    return { value: fmt.format(d), label: `${name}, ${labelFmt.format(d)}` };
  });
}

export default function PrizeModal({
  phone,
  isExistingClient = false,
  onClose,
  onGranted,
}: {
  phone: string;
  // Существующий клиент забирает подарочный ХВ — клуб и дата старта не нужны.
  isExistingClient?: boolean;
  onClose: () => void;
  onGranted: () => void;
}) {
  const { t, lang } = useT();
  const dateOptions = useMemo(() => almatyDateOptions(t, lang), [t, lang]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const clubGroups = useMemo(() => groupClubsByCity(clubs), [clubs]);
  const [clubId, setClubId] = useState("");
  const [startDate, setStartDate] = useState(dateOptions[0]?.value ?? "");
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Подарочному ХВ клуб/дата не нужны — не грузим список залов.
    if (isExistingClient) {
      setLoadingClubs(false);
      return;
    }
    let alive = true;
    getClubs()
      .then((r) => {
        if (alive) setClubs(r.clubs);
      })
      .catch((e: any) => {
        if (alive) setError(errorMessage(e, t, "prize.errLoadClubs"));
      })
      .finally(() => {
        if (alive) setLoadingClubs(false);
      });
    return () => {
      alive = false;
    };
  }, [t, isExistingClient]);

  async function submit() {
    setError("");
    if (!isExistingClient) {
      if (!clubId) return setError(t("prize.errNoClub"));
      if (!startDate) return setError(t("prize.errNoDate"));
    }
    setSubmitting(true);
    try {
      // Существующий клиент — подарочный ХВ (сервер игнорирует клуб/дату).
      const r = isExistingClient
        ? await prizeGrant()
        : await prizeGrant(clubId, startDate);
      if (r.granted || r.alreadyGranted) {
        setSuccess(true);
        onGranted();
      } else {
        setError(t("prize.errGrantFailed"));
      }
    } catch (e: any) {
      if (e instanceof ApiError) {
        setError(errorMessage(e, t, "prize.errGrant"));
      } else {
        setError(t("prize.errGrant"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-4 py-6">
      <div className="w-full max-w-md bg-card border border-line-3 rounded-[22px] p-6 max-h-[90vh] overflow-y-auto">
        {success ? (
          <div className="flex flex-col items-center text-center">
            <span className="text-5xl leading-none">🎉</span>
            <h2 className="mt-4 text-xl font-extrabold tracking-tight">
              {isExistingClient ? t("prize.gift.success.title") : t("prize.success.title")}
            </h2>
            <p className="text-muted text-sm mt-2 leading-relaxed">
              {isExistingClient ? t("prize.gift.success.body") : t("prize.success.body")}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full h-14 rounded-[14px] bg-violet-grad text-white font-extrabold text-base active:opacity-90 transition-opacity shadow-violet-btn"
            >
              {t("prize.success.done")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-4">
              <h2 className="text-xl font-extrabold tracking-tight">
                {isExistingClient ? t("prize.gift.title") : t("prize.title")}
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("prize.close")}
                className="w-8 h-8 shrink-0 rounded-full bg-white/[0.06] flex items-center justify-center active:bg-white/10 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <p className="text-muted text-sm mb-5 leading-relaxed">
              {isExistingClient ? t("prize.gift.intro") : t("prize.intro")}
            </p>

            <div className="flex flex-col gap-5">
              {isExistingClient && (
                <div className="rounded-[16px] bg-gold-t14 border border-gold/30 px-4 py-3.5 flex items-start gap-3">
                  <span className="text-2xl leading-none shrink-0">🎁</span>
                  <p className="text-[13px] text-white/85 leading-[1.5]">
                    {t("prize.gift.note")}
                  </p>
                </div>
              )}
              {!isExistingClient && (
              <div className="flex flex-col gap-2">
                <span className="text-sm text-white/80">{t("prize.clubLabel")}</span>
                {loadingClubs ? (
                  <p className="text-muted text-sm">{t("prize.loadingClubs")}</p>
                ) : clubs.length === 0 ? (
                  <p className="text-muted text-sm">{t("prize.clubsEmpty")}</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    {clubGroups.map((g) => (
                      <div key={g.city ?? "__none"} className="flex flex-col gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                          {cityLabel(g.city, lang, t)}
                        </span>
                        {g.clubs.map((c) => {
                          const selected = clubId === c.id;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => setClubId(c.id)}
                              className={`w-full text-left rounded-[14px] px-4 py-3 transition-colors ${
                                selected
                                  ? "bg-violet-grad text-white shadow-violet"
                                  : "bg-white/[0.06] text-white active:bg-white/10"
                              }`}
                            >
                              <span className="block text-sm font-medium">{c.name}</span>
                              {c.address && (
                                <span className={`block text-xs mt-0.5 ${selected ? "text-white/75" : "text-muted"}`}>
                                  {c.address}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {!isExistingClient && (
              <div className="flex flex-col gap-2">
                <span className="text-sm text-white/80">{t("prize.startDateLabel")}</span>
                <div className="flex flex-col gap-2">
                  {dateOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStartDate(opt.value)}
                      className={`w-full text-left rounded-[14px] px-4 py-3 text-sm transition-colors ${
                        startDate === opt.value
                          ? "bg-violet-grad text-white shadow-violet"
                          : "bg-white/[0.06] text-white active:bg-white/10"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {error && <p className="text-sm text-red-400">{error}</p>}

              <button
                type="button"
                onClick={submit}
                disabled={submitting || loadingClubs}
                className="w-full h-14 rounded-[14px] bg-violet-grad text-white font-extrabold text-base active:opacity-90 transition-opacity disabled:opacity-60 shadow-violet-btn"
              >
                {submitting
                  ? t("prize.submitting")
                  : isExistingClient
                    ? t("prize.gift.claim")
                    : t("prize.submit")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
